"use client";

import { useCallback, useMemo } from "react";
import {
  SourceCitationBuilder,
  type SourceField,
  type SourceValues,
} from "@/components/tool-kit/source-citation-builder";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  PREPRINT_FIELDS as F,
  formatPreprint,
  type PreprintLabels,
  preprintIsReady,
} from "@/lib/tool-kit/citation-sources-more/preprint-citation-generator";

/** English strings. A translated content file overrides these through `ui`. */
export const PREPRINT_CITATION_GENERATOR_STRINGS = {
  formHeading: "Preprint details",
  resultsHeading: "Your citations",
  emptyPrompt: "Enter the preprint's title to see your citations.",
  copy: "Copy",
  copied: "Copied!",
  tryExample: "Try an example",
  clear: "Clear",
  authorsLabel: "Author(s)",
  authorsPlaceholder: "Kathy Fiallo",
  authorsHint:
    "One per line, as First Last or Last, First. The authors on the preprint itself, in the order shown.",
  titleLabel: "Preprint title",
  titlePlaceholder:
    "The influence of auditory processing difficulties on perceptual learning",
  titleHint:
    "Type it in sentence case: capitalize only the first word, the first word after a colon and proper nouns. MLA and Chicago get title case automatically.",
  serverLabel: "Preprint server",
  serverBiorxiv: "bioRxiv",
  serverMedrxiv: "medRxiv",
  serverSsrn: "SSRN",
  serverPsyarxiv: "PsyArXiv",
  serverOsf: "OSF Preprints",
  serverResearchSquare: "Research Square",
  serverOtherLabel: "A different server (optional)",
  serverOtherPlaceholder: "EarthArXiv",
  serverOtherHint:
    "Type the server's name to use it instead of the one chosen above. For arXiv, the arXiv generator is the better fit.",
  postedLabel: "Date posted",
  postedPlaceholder: "YYYY-MM-DD",
  postedHint:
    "The date the version you read went online, shown on the preprint's page. A year alone works too.",
  linkLabel: "DOI or URL",
  linkPlaceholder: "10.31234/osf.io/p723u",
  linkHint:
    "Paste the DOI from the server's own page, with or without https://doi.org/. Use the address of the page if there is no DOI.",
  apaLabel: "APA 7th edition",
  mlaLabel: "MLA 9th edition",
  chicagoLabel: "Chicago 18th edition (notes and bibliography)",
  referenceList: "Reference list",
  parenthetical: "In-text citation (parenthetical)",
  narrative: "In-text citation (narrative)",
  worksCited: "Works Cited",
  inText: "In-text citation",
  footnote: "Footnote",
  shortNote: "Shortened note",
  bibliography: "Bibliography",
  apaNote:
    "APA 7 puts the preprint's title in italics and names the server as the source, in plain type, followed by the DOI. If a journal has since published the paper, cite the published version instead.",
  mlaNote:
    "MLA 9 has no preprint example of its own. This follows the MLA Style Center's answer on working papers: the server is the container, in italics, and the optional label at the end says Preprint.",
  chicagoNote:
    "Chicago 18 (§14.76) treats a preprint as unpublished material and marks it Preprint, with the server and the date. Putting those details in parentheses in a note is the usual Chicago pattern for notes.",
  example: {
    authors: "Kathy Fiallo\nAlexia Galati",
    title:
      "The influence of auditory processing difficulties on perceptual learning",
    server: "psyarxiv",
    posted: "2022-09-04",
    link: "10.31234/osf.io/p723u",
  },
} as const;

type Strings = typeof PREPRINT_CITATION_GENERATOR_STRINGS;

function buildFields(strings: Strings): SourceField[] {
  return [
    {
      id: F.authors,
      kind: "people",
      label: strings.authorsLabel,
      placeholder: strings.authorsPlaceholder,
      hint: strings.authorsHint,
    },
    {
      id: F.title,
      kind: "text",
      label: strings.titleLabel,
      placeholder: strings.titlePlaceholder,
      hint: strings.titleHint,
    },
    {
      id: F.server,
      kind: "select",
      label: strings.serverLabel,
      options: [
        { value: "biorxiv", label: strings.serverBiorxiv },
        { value: "medrxiv", label: strings.serverMedrxiv },
        { value: "ssrn", label: strings.serverSsrn },
        { value: "psyarxiv", label: strings.serverPsyarxiv },
        { value: "osf", label: strings.serverOsf },
        { value: "researchsquare", label: strings.serverResearchSquare },
      ],
    },
    {
      id: F.serverOther,
      kind: "text",
      label: strings.serverOtherLabel,
      placeholder: strings.serverOtherPlaceholder,
      hint: strings.serverOtherHint,
    },
    {
      id: F.posted,
      kind: "date",
      label: strings.postedLabel,
      placeholder: strings.postedPlaceholder,
      hint: strings.postedHint,
    },
    {
      id: F.link,
      kind: "url",
      label: strings.linkLabel,
      placeholder: strings.linkPlaceholder,
      hint: strings.linkHint,
    },
  ];
}

/** The widget's strings, as the formatter's labels. */
export function preprintLabels(strings: Strings): PreprintLabels {
  return {
    apa: strings.apaLabel,
    mla: strings.mlaLabel,
    chicago: strings.chicagoLabel,
    referenceList: strings.referenceList,
    parenthetical: strings.parenthetical,
    narrative: strings.narrative,
    worksCited: strings.worksCited,
    inText: strings.inText,
    footnote: strings.footnote,
    shortNote: strings.shortNote,
    bibliography: strings.bibliography,
    apaNote: strings.apaNote,
    mlaNote: strings.mlaNote,
    chicagoNote: strings.chicagoNote,
  };
}

export default function PreprintCitationGeneratorWidget() {
  const strings = useToolUi(PREPRINT_CITATION_GENERATOR_STRINGS);
  const fields = useMemo(() => buildFields(strings), [strings]);
  const labels = useMemo(() => preprintLabels(strings), [strings]);
  const format = useCallback(
    (values: SourceValues) => formatPreprint(values, labels),
    [labels]
  );

  return (
    <SourceCitationBuilder
      example={strings.example}
      fields={fields}
      format={format}
      isReady={preprintIsReady}
      strings={strings}
    />
  );
}
